import { z } from 'zod';

/**
 * Two distinct schemas for Part because the backend rejects post-
 * creation mutation of code, partTypeId, and ownerCompanyId — those
 * would invalidate FK relationships with stock lots and movements.
 *
 *   partCreateSchema — full payload, used by the create form.
 *   partUpdateSchema — editable subset (name, manufacturer,
 *                      manufacturerPartNumber, unit, min/maxStock,
 *                      notes, isActive).
 *
 * Optional strings normalised to `null`. The min/maxStock fields
 * coerce empty/string/number to null|nonNegative int, matching the
 * backend DTO @IsInt() @Min(0).
 */
const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v));

const optionalUuid = z
  .string()
  .uuid()
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

/**
 * Optional non-negative int that empties to `null`. Used for
 * min/maxStockGlobal where the form sends "" or a numeric string;
 * we coerce to int and reject negatives. Zero is allowed (a part
 * with maxStockGlobal=0 means "no max — track usage but do not
 * cap"; minStockGlobal=0 means no low-stock alert).
 */
const optionalNonNegativeInt = z
  .union([z.string(), z.number(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  })
  .refine((v) => v === null || v >= 0, {
    message: 'Must be a non-negative integer',
  });

/** Fields editable in BOTH create and update modes. */
const editableFields = {
  name: z.string().min(1).max(200),
  manufacturer: optionalString(200),
  manufacturerPartNumber: optionalString(100),
  unit: optionalString(20),
  minStockGlobal: optionalNonNegativeInt,
  maxStockGlobal: optionalNonNegativeInt,
  notes: optionalString(2000),
};

export const partCreateSchema = z.object({
  ...editableFields,
  // Code: same regex as backend, runs locally so form-level errors
  // land before a network round-trip.
  code: z
    .string()
    .min(2)
    .max(50)
    .regex(/^[A-Z0-9_-]+$/, 'Code: uppercase letters, digits, _ or - only'),
  partTypeId: z.string().uuid(),
  ownerCompanyId: optionalUuid,
});

export type PartCreateValues = z.infer<typeof partCreateSchema>;

export const partUpdateSchema = z.object({
  ...editableFields,
  isActive: z.boolean().optional(),
});

export type PartUpdateValues = z.infer<typeof partUpdateSchema>;
